import classNames from "classnames";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { getAdaptiveTextColor } from "PFCore/utilities/color";
import { IconName } from "PFTheme/graphics/icons";
import { Color } from "PFTypes";
import { AriaAttributes, forwardRef, SyntheticEvent, useMemo } from "react";

import { Typography } from "../typography";
import css from "./pill.module.scss";

export type PillKind = "danger" | "warning" | "success" | "general" | "regular" | "ghost";

const ICON_COLORS: Record<PillKind, Color> = {
  danger: "paletteRed1",
  warning: "paletteOrange1",
  success: "paletteGreen1",
  general: "paletteBlue0",
  regular: "paletteBlue0",
  ghost: "paletteBlue0"
};

type ConditionalProps =
  | {
      icon?: IconName;
      text: string;
      children?: never;
    }
  | {
      icon?: IconName;
      text?: never;
      children: React.ReactNode;
    }
  | {
      icon: IconName;
      text?: never;
      children?: never;
    };

type VariantProps = { kind?: PillKind; color?: never } | { color?: string; kind?: never };

export type PillProps = {
  small?: boolean;
  onDelete?: (event: SyntheticEvent) => void;
  onDeleteIcon?: Extract<IconName, "cross" | "remove">;
  onDeleteKeyDown?: (event: SyntheticEvent) => void;
  deleteTitle?: string;
  classes?: {
    root?: string;
    deleteIcon?: string;
    text?: string;
  };
  style?: React.CSSProperties;
  title?: string;
  clipOverflowText?: boolean;
  disabled?: boolean;
  tabIndex?: number;
} & VariantProps &
  ConditionalProps &
  AriaAttributes;

export const Pill = forwardRef<HTMLDivElement, PillProps>(
  (
    {
      icon,
      text,
      small = false,
      onDelete,
      onDeleteIcon = "cross",
      onDeleteKeyDown,
      deleteTitle,
      kind = "regular",
      color,
      children,
      classes = {},
      style,
      title,
      "aria-label": ariaLabel,
      clipOverflowText,
      disabled,
      tabIndex
    },
    ref
  ) => {
    const pillClassNames = classNames(
      css.root,
      {
        [css.iconOnly]: icon && !text && !children,
        [css[`pill-${kind}`]]: !color,
        [css.smallPill]: small,
        [css.withDeleteAction]: onDelete
      },
      classes.root
    );

    const isChildrenString = typeof children === "string";
    const colorSet = useMemo(
      () =>
        color
          ? {
              color: getAdaptiveTextColor(color),
              backgroundColor: color
            }
          : {},
      [color]
    );

    return (
      <Inline
        center
        gap="spacingSm"
        ref={ref}
        className={pillClassNames}
        aria-label={ariaLabel ?? text}
        style={{
          ...colorSet,
          ...style
        }}
      >
        {onDelete && (
          <ActionIcon
            name={onDeleteIcon}
            size={small ? "xxs" : "sm"}
            onClick={onDelete}
            onKeyDown={onDeleteKeyDown}
            title={deleteTitle}
            aria-label={deleteTitle}
            color={ICON_COLORS[kind]}
            classes={{ root: classes.deleteIcon }}
            disabled={disabled}
            tabIndex={tabIndex}
          />
        )}
        {icon && <Icon name={icon} size={small ? "xs" : "sm"} />}
        {isChildrenString || text ? (
          <Typography
            variant={small ? "labelRegular" : "bodyRegular"}
            tag="span"
            title={title || text || (isChildrenString ? children : undefined)}
            className={classNames(css.text, classes.text)}
            clipOverflow={clipOverflowText ?? true}
          >
            {children ?? text}
          </Typography>
        ) : (
          children
        )}
      </Inline>
    );
  }
);

Pill.displayName = "Pill";
